import React from 'react';

import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';
import type { PastMeetingOccurrence } from '@atlassian/search-client/past-meeting-occurrences/types';
import { isAnyCodeSearchEnabled } from '@atlassian/search-common/code-search-mode';

import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import type { SearchResultProps } from '../../../../../common/types';
import { useLogException } from '../../../../../controllers/error-boundary/utils';
import type { ResultDerivedDataProps } from '../../result-derived-data';
import type { searchResultRenderer_AtlassianSearchPage$key } from './__generated__/searchResultRenderer_AtlassianSearchPage.graphql';
import { isSearchAssetsObject, SearchAssetsObject } from './search-assets-object';
import {
	isSearchAssetsObjectSchema,
	SearchAssetsObjectSchema,
} from './search-assets-object-schema';
import { isSearchAssetsObjectType, SearchAssetsObjectType } from './search-assets-object-type';
import { isSearchResultAtlasGoal, SearchAtlasGoal } from './search-atlas-goal';
import { isSearchAtlasProject, SearchAtlasProject } from './search-atlas-project';
import { isSearchCompassComponent, SearchCompassComponent } from './search-compass-component';
import {
	isSearchConfluencePageBlogAttachment,
	SearchConfluencePageBlogAttachment,
} from './search-confluence-page-blog-attachment';
import { isSearchConfluenceSpace, SearchConfluenceSpace } from './search-confluence-space';
import { SearchDefaultRenderer } from './search-default-renderer';
import { isSearchFederatedResult, SearchFederatedResult } from './search-federated-result';
import { isSearchGraphCode, SearchGraphCode } from './search-graph-code';
import { isSearchGraphResult, SearchGraphResult } from './search-graph-result';
import { isSearchJiraBoard, SearchJiraBoard } from './search-jira-board';
import { isSearchJiraDashboard, SearchJiraDashboard } from './search-jira-dashboard';
import { isSearchJiraFilter, SearchJiraFilter } from './search-jira-filter';
import {
	isSearchJiraIssue,
	type JiraIssueStatusAndColorName,
	SearchJiraIssue,
} from './search-jira-issue';
import { isSearchJiraPlan, SearchJiraPlan } from './search-jira-plan';
import { isSearchJiraProject, SearchJiraProject } from './search-jira-project';
import { isSearchLoomEntity, SearchLoomEntity } from './search-loom-entity';
import { isSearchLoomVideo, SearchLoomVideo } from './search-loom-video';
import { isSearchMercuryAsk, SearchMercuryAsk } from './search-mercury-ask';
import { isSearchMercuryFocusArea, SearchMercuryFocusArea } from './search-mercury-focus-area';
import {
	isSearchMercuryFocusAreaUpdate,
	SearchMercuryFocusAreaUpdate,
} from './search-mercury-focus-area-update';
import { isSearchMercuryRisk, SearchMercuryRisk } from './search-mercury-risk';
import { isSearchPassionfruitAsk, SearchPassionfruitAsk } from './search-passionfruit-ask';
import { isSearchTalentPosition, SearchTalentPosition } from './search-talent-position';
import { isSearchTrello, SearchTrello } from './search-trello';

export type SearchResultRendererProps =
	SearchResultProps<searchResultRenderer_AtlassianSearchPage$key> &
		ResultDerivedDataProps & {
			thirdPartyConfigs: ThirdPartyConfigsBootstrap;
			compassTeamName?: string | null; // Optional, used for Compass teams
			jiraIssueStatus?: JiraIssueStatusAndColorName | null; // Optional, used for Jira issues
			jiraLinkedPRs?: IssueLinkedPRsEntry; // Optional, used for Jira issue linked PRs
			pastMeetingOccurrences?: PastMeetingOccurrence[] | null; // Optional, used for recurring meeting videos
			smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
			mainContentWidth?: number;
			/**
			 * When true, leaf renderers that support a lite variant will render
			 * their lite-mode form instead of the standard SearchResult card. The
			 * specific lite-mode form is defined by each leaf renderer (it may be a
			 * table row, a compact card, or any other variant tailored to lite
			 * mode). Set by the parent list renderer (SearchResultsListLiteRenderer)
			 * only when its own preconditions are met.
			 */
			isLiteMode?: boolean;
		};

const SearchResultRenderer = ({
	node: fragmentRef,
	thirdPartyConfigs,
	compassTeamName,
	jiraIssueStatus,
	jiraLinkedPRs,
	pastMeetingOccurrences,
	smartAnswersPortalRef,
	isLiteMode,
	...props
}: SearchResultRendererProps): React.JSX.Element => {
	// Casting to `any` to avoid type errors with type guards
	// TODO: Improve type guards to avoid casting
	const node: any = useFragment(
		graphql`
			fragment searchResultRenderer_AtlassianSearchPage on SearchResult
			@argumentDefinitions(
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeFastJiraHydration: { type: "Boolean", defaultValue: false }
				includeAssets: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeDatasourceId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includeRisk: { type: "Boolean", defaultValue: false }
				includeCodeSearch: { type: "Boolean", defaultValue: false }
				includeMercuryAsk: { type: "Boolean", defaultValue: false }
				includeCaseEmbeddedComments: { type: "Boolean", defaultValue: false }
			) {
				__typename
				id
				...searchAtlasGoal_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchAtlasProject_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchFederatedResult_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchCompassComponent_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchConfluencePageBlogAttachment_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@arguments(includeNavBoostScore: $includeNavBoostScore)
				...searchConfluenceSpace_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchGraphResult_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@arguments(
						includeThirdPartyId: $includeThirdPartyId
						includeDatasourceId: $includeDatasourceId
						includeCaseEmbeddedComments: $includeCaseEmbeddedComments
					)
				...searchGraphCode_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includeCodeSearch)
				...searchJiraIssue_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@arguments(
						includeJiraLiteModeFields: $includeJiraLiteModeFields
						includeFastJiraHydration: $includeFastJiraHydration
					)
				...searchJiraProject_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@arguments(includeJiraLiteModeFields: $includeJiraLiteModeFields)
				...searchJiraPlan_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchJiraBoard_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchJiraDashboard_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchJiraFilter_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchLoomEntity_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchLoomVideo_AtlassianSearchPage @dangerously_unaliased_fixme
				...searchMercuryFocusArea_AtlassianSearchPage
				...searchMercuryFocusAreaUpdate_AtlassianSearchPage
				...searchMercuryRisk_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includeRisk)
				...searchMercuryAsk_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includeMercuryAsk)
				...searchPassionfruitAsk_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includePassionfruitAsk)
				...searchTrello_AtlassianSearchPage
				...searchAssetsObjectSchema_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includeAssets)
				...searchAssetsObjectType_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includeAssets)
				...searchAssetsObject_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includeAssets)
				...searchDefaultRenderer_AtlassianSearchPage
				...searchTalentPosition_AtlassianSearchPage
					@dangerously_unaliased_fixme
					@include(if: $includePositions)
			}
		`,
		fragmentRef,
	);

	const logException = useLogException();

	// Try/catch because AnyAri.parse throws on malformed ARIs
	try {
		if (!node) {
			throw new Error('ResultRecord: node is undefined');
		}
		if (isSearchConfluenceSpace(node)) {
			return <SearchConfluenceSpace {...props} node={node} />;
		}
		if (isSearchConfluencePageBlogAttachment(node)) {
			return (
				<SearchConfluencePageBlogAttachment
					{...props}
					node={node}
					thirdPartyConfigs={thirdPartyConfigs}
					smartAnswersPortalRef={smartAnswersPortalRef}
				/>
			);
		}
		if (isSearchResultAtlasGoal(node)) {
			return <SearchAtlasGoal {...props} node={node} />;
		}
		if (isSearchAtlasProject(node)) {
			return <SearchAtlasProject {...props} node={node} />;
		}
		if (isSearchCompassComponent(node)) {
			return <SearchCompassComponent {...props} node={node} compassTeamName={compassTeamName} />;
		}
		if (isSearchJiraProject(node)) {
			return <SearchJiraProject {...props} node={node} shouldRenderTableResult={isLiteMode} />;
		}
		if (isSearchJiraIssue(node)) {
			return (
				<SearchJiraIssue
					{...props}
					node={node}
					jiraIssueStatus={jiraIssueStatus}
					jiraLinkedPRs={jiraLinkedPRs}
					shouldRenderTableResult={isLiteMode}
				/>
			);
		}
		if (isSearchJiraPlan(node)) {
			return <SearchJiraPlan {...props} node={node} />;
		}
		if (isSearchJiraBoard(node)) {
			return <SearchJiraBoard {...props} node={node} />;
		}
		if (isSearchJiraFilter(node)) {
			return <SearchJiraFilter {...props} node={node} />;
		}
		if (isSearchJiraDashboard(node)) {
			return <SearchJiraDashboard {...props} node={node} />;
		}
		if (isSearchLoomVideo(node)) {
			return <SearchLoomVideo {...props} node={node} />;
		}
		if (isSearchLoomEntity(node)) {
			return <SearchLoomEntity {...props} node={node} />;
		}
		if (isSearchTrello(node)) {
			return <SearchTrello {...props} node={node} />;
		}
		if (isSearchMercuryFocusArea(node)) {
			return <SearchMercuryFocusArea {...props} node={node} />;
		}
		if (isSearchMercuryFocusAreaUpdate(node)) {
			return <SearchMercuryFocusAreaUpdate {...props} node={node} />;
		}
		if (isSearchMercuryRisk(node) && fg('platform_mercury_risk_search_ui_enabled')) {
			return <SearchMercuryRisk {...props} node={node} />;
		}
		if (isSearchMercuryAsk(node) && fg('platform_mercury_ask_search_ui_enabled')) {
			return <SearchMercuryAsk {...props} node={node} />;
		}
		if (
			node.__typename === 'SearchResultMercuryOrganization' ||
			node.__typename === 'SearchResultMercuryOrganizationStatusUpdate'
		) {
			return <SearchDefaultRenderer {...props} node={node} />;
		}
		if (isSearchPassionfruitAsk(node) && fg('platform_passionfruit_search_onboarding_enabled')) {
			return <SearchPassionfruitAsk {...props} node={node} />;
		}
		if (isSearchFederatedResult(node)) {
			return <SearchFederatedResult {...props} node={node} thirdPartyConfigs={thirdPartyConfigs} />;
		}
		if (isSearchAssetsObjectSchema(node) && fg('assets_rovo_search_all')) {
			return <SearchAssetsObjectSchema {...props} node={node} />;
		}
		if (isSearchAssetsObjectType(node) && fg('assets_rovo_search_all')) {
			return <SearchAssetsObjectType {...props} node={node} />;
		}
		if (isSearchAssetsObject(node) && fg('assets_rovo_search_all')) {
			return <SearchAssetsObject {...props} node={node} />;
		}
		if (isSearchTalentPosition(node) && fg('talent_global_search')) {
			return <SearchTalentPosition {...props} node={node} />;
		}
		if (isSearchGraphCode(node) && isAnyCodeSearchEnabled()) {
			return <SearchGraphCode {...props} node={node} />;
		}
		if (node.id && isSearchGraphResult(node)) {
			return (
				<SearchGraphResult
					{...props}
					node={node}
					thirdPartyConfigs={thirdPartyConfigs}
					pastMeetingOccurrences={pastMeetingOccurrences}
				/>
			);
		}
		throw new Error(`SearchResultRenderer: unsupported node typename=${node['__typename']}`);
	} catch (e) {
		logException(e);
		return <SearchDefaultRenderer {...props} node={node} />;
	}
};

export { SearchResultRenderer };
